import { Component, ElementRef, Input, ViewChild } from '@angular/core';
import { Tank } from '../Tank';
import { I18NService } from '../../../shared/i18n/i18n.service';
import * as Highcharts from 'highcharts/highstock';
import HC_exporting from 'highcharts/modules/exporting';
import HC_exporting_data from 'highcharts/modules/export-data';
import HC_xrange from 'highcharts/modules/xrange';

HC_exporting(Highcharts);
HC_exporting_data(Highcharts);
HC_xrange(Highcharts);

@Component({
  selector: 'app-tank-chart',
  templateUrl: './tank-chart.component.html',
  styleUrls: ['./tank-chart.component.css']
})
export class TankChartComponent {

  Highcharts: typeof Highcharts = Highcharts;
  chartConstructor: string = 'chart';
  chartOptions: Highcharts.Options = {  };
  chartOptionsProducts: Highcharts.Options = {  };
  series: any[];
  chart: Highcharts.Chart;
  chartCallback: Highcharts.ChartCallbackFunction;
  @ViewChild('tanks', { static: true }) chartElement: ElementRef;

  colors = {
    "90": "#8a6a4c",
    "91": "#04afa0",
    "92": "#b744d3",
    "93": "#fab300",
    "94": "#043f91",
    "95": "#2e6b52",
    "97": "#467a10",
    "98": "#96bd1e",
    "99": "#f2684e"
  }

  constructor(private i18nService: I18NService) {
    var self = this;
    this.chartCallback = chart => { self.chart = chart; };
  }

  @Input()
  iplace: string;

  @Input()
  set tanks(tanks : Tank[]) {
    this.updateValues(tanks);
  }

  private updateValues(tanks : Tank[]) {
    this.chartConstructor = "chart";
    this.chartOptions = this.getColumnChartConfig(tanks);
    this.chartOptionsProducts = this.getProductsChartConfig(tanks);
  }

  private getColumnChartConfig(tanks : Tank[]): Highcharts.Options {
    let categories = [];
    let series = this.parseColumnSeries(tanks);
    let chartOptions = this.getBaseChartConfig();

    let waterVol = this.i18nService.get("water");
    let temperature = this.i18nService.get("temperature");

    tanks.forEach(tank => {
      categories.push(this.i18nService.get("tank") + " " + tank.localTankId + " - " + this.i18nService.get(tank.productId) + '<br/>' + Highcharts.dateFormat("%H:%M %e/%m", tank.updated));
    });

    chartOptions.xAxis = {
      categories : categories
    }

    chartOptions.tooltip = {
      shared: true,
      formatter: function() {
        return ['<span style="padding-bottom:3px;"><b>' + this.x.toString().split('<br/>')[0] + '</b></span><br/>'].concat(
          this.points ? '<span style="padding-bottom:3px;">' +
                        '<i>' + this.points[1].series.name + ':</i> ' + Highcharts.numberFormat(this.points[1].y, 2, '.', ',') + ' L</span><br/>' +
                        '<span style="padding-bottom:3px;">' +
                        '<i>' + this.points[0].series.name + ':</i> ' + Highcharts.numberFormat(this.points[0].y, 2, '.', ',') + ' L</span><br/>'
              : [] )
          .concat('<span style="padding-bottom:3px;"><i>Total:</i> ' + Highcharts.numberFormat(this.points[0].total, 2, '.', ',') + ' L</span><br/>')
          .concat('<span style="padding-bottom:3px;"><i>' + temperature  + ':</i> ' + this.points[0].point['temperature'] + ' º</span><br/>' +
            '<span style="padding-bottom:3px;"><i>' + waterVol  + ':</i> ' + this.points[0].point['water'] +' mm</span><br/>')
      }
    }

    chartOptions.series = series;

    return chartOptions;
  }

  private getProductsChartConfig(tanks : Tank[]): Highcharts.Options {
    let categories = [];
    let series = this.parseProductsSeries(tanks);
    let chartOptions = this.getBaseChartConfig();

    tanks.forEach(tank => {
      if(!categories.find(item => tank.productId == item))
        categories.push(tank.productId);
    });

    categories
      .sort((a, b) => a < b ? -1 : a > b ? 1 : 0)
      .forEach((categorie, index) => {
        categories[index] = this.i18nService.get(categorie);
      });

    chartOptions.xAxis = {
      categories : categories
    }

    chartOptions.tooltip = {
      shared: true,
      useHTML: true,
      formatter: function() {

        return ['<span style="display:inline-block;padding-bottom:3px;"><b>' + this.x + '</b></span><br/>'].concat(
          this.points ?
            this.points.map(function(point) {
              return '<span style="display:inline-block;padding-bottom:3px;">' +
                      '<i>' + point.series.name + ':</i> ' + Highcharts.numberFormat(point.y, 2, '.', ',') + ' L' +
                     '</span><br/>';
            }) : []
        ).concat('<i>Total:</i> ' + Highcharts.numberFormat(this.points[0].total, 2, '.', ',') + ' L<br/>')
      }
    }

    chartOptions.series = series;

    return chartOptions;
  }

  private getBaseChartConfig(): Highcharts.Options {
    return {
      title: '',
      chart: {
        type: 'column',
      },

      rangeSelector: {
        inputEnabled: false,
        enabled: false
      },
      yAxis: {
        min: 0,
        title: {
          text: this.i18nService.get("volume") + ' (L)',
        },
        labels: {
          formatter: function () {
            return this.value + '';
          }
        },
        stackLabels: {
          enabled: false,
        }
      },
      plotOptions: {
        column: {
          stacking: 'normal',
          dataLabels: {
            enabled: true,
            style: {
              fontWeight: 'roman',
              color: '#000000',
              textOutline: '0px'
            },
          }
        },
        series: {
          stickyTracking: false,
          events: {
            mouseOut: function() {
              this.chart.tooltip.hide();
            }
          }
        }
      },
      exporting: {
        enabled: false
      },
      credits: {
        enabled: false
      },
      legend: {
        enabled: false
      },
    } as Highcharts.Options;
  }

  private parseColumnSeries(tanks: Tank[]): any[]{
    let volume = [], toFill = [];

    tanks.forEach(tank => {
      volume.push({name: this.i18nService.get(tank.productId),
                  y: Math.round(tank.productVolume * 100) / 100,
                  color: this.colors[tank.productId]});
      let point = (tank.capacity - tank.productVolume) < 0 ? 0 : (tank.capacity - tank.productVolume);
      toFill.push({y: Math.round(point * 100) / 100,
                  updated: tank.updated,
                  temperature: tank.temperature,
                  water: tank.waterHeight,
                  color: '#E7E7E7'});
    });

    let series = [{
      name: this.i18nService.get('toFill'),
      data: toFill
      }, {
      name: this.i18nService.get('volume'),
      data: volume
      }];

    return series;
  }

  private parseProductsSeries(tanks: Tank[]): any[] {
    let volume = [], toFill = [], products = [];
    let totalVolume = new Map(), totalToFill = new Map();

    tanks.forEach((tank) => {
        totalVolume[tank.productId] = totalVolume.get(tank.productId) || totalVolume.set(tank.productId, 0);
        totalVolume.set(tank.productId, Number(totalVolume.get(tank.productId)) + Number(tank.productVolume));

        totalToFill[tank.productId] = totalToFill.get(tank.productId) || totalToFill.set(tank.productId, 0);
        totalToFill.set(tank.productId, Number(totalToFill.get(tank.productId)) + Number(tank.capacity));

        if (!products.find(item => tank.productId == item))
          products.push(tank.productId);

    });

    products
      .sort((a, b) => a < b ? -1 : a > b ? 1 : 0)
      .forEach(productId => {
        volume.push({name: this.i18nService.get(productId),
                    y: Math.round(totalVolume.get(productId) * 100) / 100,
                    color: this.colors[productId]});
        let point = (totalToFill.get(productId) - totalVolume.get(productId)) < 0 ? 0 : (totalToFill.get(productId) - totalVolume.get(productId));
        toFill.push({y: Math.round(point * 100) / 100,
                     color: '#E7E7E7'});
      });

    let series = [{
      name: this.i18nService.get('toFill'),
      data: toFill
    }, {
      name: this.i18nService.get('volume'),
      data: volume
    }];

    return series;
  }
}
